import React from 'react';
import {PoppaysLoader} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import PERMISSIONS from '@/config/permissions';
import {VettableCreator} from '@/types/vettableCreatorsResponse';
import {PROFILE_SOURCE} from '@/components/creator-profile/config/profileSources';
import useProfileSource from '@/components/creator-profile/hooks/use-profile-source';
import CREATOR_PROFILE_TEST_IDS from '@/components/creator-profile/testIds';
import CreatorVettingReport from '@/components/creator-vetting-report';
import ProfileVettingEmptyState from '@/components/creator-vetting-report/components/profile-vetting-empty-state';
import ReportLastUpdated from '@/components/creator-vetting-report/components/report-last-updated';
import useCreatorBrandSafetyVettingsQuery from '@/hooks/queries/use-creator-brand-safety-vettings-query';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';
import useVettingReportQuery from '@/hooks/queries/use-vetting-report-query';
import useBrandId from '@/hooks/use-brand-id';
import useDashboardUserPermissions from '@/hooks/use-dashboard-user-permissions';
import {CreatorProfileTabContentProps} from '../../CreatorProfileTabContentProps';
import TabWrapper from '../tab-wrapper';
import FlagsEmptyState from './FlagsEmptyState';
import styles from './FlagsTab.module.scss';

function FlagsTabReport({
  brandId,
  creatorId,
  reportId,
  scope,
}: {
  brandId: string;
  creatorId: string;
  reportId: string;
  scope?: 'brand' | 'agency';
}) {
  const {
    data: vettingReport,
    isLoadingFullData,
    isError,
  } = useVettingReportQuery({
    brandId,
    creatorId,
    reportId,
    scope,
  });

  if (isError) {
    return (
      <ProfileVettingEmptyState
        title={translate('Failed to load report')}
        subtitle={translate(
          'Something went wrong while loading the report. Please try again later.'
        )}
      />
    );
  }

  if (!vettingReport) {
    return (
      <div className={styles.loaderWrapper}>
        <PoppaysLoader />
      </div>
    );
  }

  return (
    <div className={styles.reportContainer}>
      <ReportLastUpdated
        lastUpdated={vettingReport.timeRange.end}
        socialNetworks={vettingReport.socialNetworks}
      />
      <CreatorVettingReport
        vettingReport={vettingReport}
        showBackButton={false}
        showProfileInfo={false}
        showSidebar={false}
        isLoadingFullData={isLoadingFullData}
      />
    </div>
  );
}

function FlagsTab(props: CreatorProfileTabContentProps) {
  const {profile, creatorProfileAbilities} = props;
  const brandId = useBrandId();
  const source = useProfileSource({
    displayPublicDataOnly: creatorProfileAbilities.displayPublicDataOnly,
  });
  // In the CRM (agency) context, fall back to a sibling org brand's report
  // when the current brand has none. Decision stays per-brand.
  const scope = source === PROFILE_SOURCE.CRM ? 'agency' : undefined;
  const {dashboardUser} = useDashboardUserQuery({});
  const {createVetting: canCreateVetting} = useDashboardUserPermissions(
    [PERMISSIONS.VETTING.CREATE],
    dashboardUser?.permissions
  );

  const {data: vettings, isLoading} = useCreatorBrandSafetyVettingsQuery({
    brandId,
    profileId: profile.id,
    enabled: !!brandId,
    scope,
  });

  const vetting = vettings?.[0];

  const buildVettableCreator = (): VettableCreator => ({
    id: profile.id,
    fullName: profile.displayName,
    email: profile.email || null,
    emails: null,
    profileImage: profile.imageUrl,
  });

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className={styles.loaderWrapper}>
          <PoppaysLoader />
        </div>
      );
    }

    if (!vetting) {
      return (
        <FlagsEmptyState
          brandId={brandId}
          creator={buildVettableCreator()}
          canCreateVetting={canCreateVetting}
        />
      );
    }

    if (vetting.reportStatus === 'ready') {
      return (
        <FlagsTabReport
          brandId={brandId}
          creatorId={profile.id}
          reportId={vetting.reportId}
          scope={scope}
        />
      );
    }

    if (
      vetting.reportStatus === 'failed_scraping' ||
      vetting.reportStatus === 'failed_vetting'
    ) {
      return (
        <ProfileVettingEmptyState
          title={translate('Report creation failed')}
          subtitle={translate(
            "Something went wrong while analyzing this creator's content. Please try again later."
          )}
        />
      );
    }

    if (vetting.status === 'vetting_unavailable') {
      return (
        <ProfileVettingEmptyState
          title={translate('Vetting unavailable')}
          subtitle={translate('Vetting is not available for this creator.')}
        />
      );
    }

    return (
      <ProfileVettingEmptyState
        title={translate('Report creation in progress')}
        subtitle={translate(
          "We're analyzing this creator's content. This usually takes a few minutes."
        )}
      />
    );
  };

  return (
    <TabWrapper testID={CREATOR_PROFILE_TEST_IDS.TAB__FLAGS}>
      {renderContent()}
    </TabWrapper>
  );
}

export default FlagsTab;
